// What this business's plan includes, for the screens (the server checks every request itself;
// this only hides what the plan doesn't have, so nobody taps into a "not in your plan" message).
// West Coast Wireless (the home business) and a server that runs one store have everything,
// including the integrations built on West Coast Wireless's own accounts.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { get, onSaved } from './api';
import { useSession } from './session';

export type PlanFeature = 'tech_assignment' | 'visit_booking' | 'pnl' | 'special_orders' | 'buybacks' | 'company_reports' | 'transfers' | 'audit_log';
export type Industry = 'cellphone' | 'computer' | 'auto' | 'vacuum';

const EVERY_FEATURE: PlanFeature[] = ['tech_assignment', 'visit_booking', 'pnl', 'special_orders', 'buybacks', 'company_reports', 'transfers', 'audit_log'];

export interface PlanInfo {
  home: boolean;
  plan: string | null;
  plan_name: string | null;
  features: PlanFeature[];
  limits: { users: number | null; locations: number | null };
  usage: { users: number; locations: number };
  status: string;
}

interface PlanValue extends PlanInfo {
  /** A business that subscribes to Store OS (not West Coast Wireless, not a one-store server). */
  subscriber: boolean;
  /** The kind of shop: its job names, menus and screens. */
  industry: Industry;
  has: (f: PlanFeature) => boolean;
  reload: () => void;
}

const EVERYTHING: PlanInfo = { home: true, plan: null, plan_name: null, features: EVERY_FEATURE, limits: { users: null, locations: null }, usage: { users: 0, locations: 0 }, status: 'active' };
/** A subscriber's plan before it has loaded: nothing gated shows yet (it appears once it loads, never the other way round). */
const LOADING: PlanInfo = { home: false, plan: null, plan_name: null, features: [], limits: { users: null, locations: null }, usage: { users: 0, locations: 0 }, status: 'loading' };
/**
 * The public demo shows what a shop that subscribes gets: every plan's features, and none of the
 * tools built on West Coast Wireless's own accounts (texting, its suppliers' parts search, unlock
 * orders, Clover, Shopify points, the assistant).
 */
const DEMO: PlanInfo = { ...EVERYTHING, home: false };

const Ctx = createContext<PlanValue>({ ...EVERYTHING, subscriber: false, industry: 'cellphone', has: () => true, reload: () => undefined });

export function PlanProvider({ children }: { children: ReactNode }) {
  const { status, me } = useSession();
  const subscriber = !!status?.platform?.business && !status.platform.business.home;
  const base = status?.demo_site ? DEMO : EVERYTHING;
  const [loaded, setInfo] = useState<PlanInfo | null>(null);
  const load = useCallback(() => {
    if (!subscriber || !me) {
      setInfo(subscriber ? null : base);
      return;
    }
    get<PlanInfo>('/api/platform/features').then(setInfo).catch(() => undefined);
  }, [subscriber, base, me?.employee.id]);
  const info = loaded ?? (subscriber ? LOADING : base);
  useEffect(() => { load(); }, [load]);
  // People, stores and plan changes move the counts and the features.
  useEffect(() => onSaved((path) => { if (subscriber && /^\/api\/(platform\/billing|employees|locations)/.test(path)) load(); }), [subscriber, load]);
  const value = useMemo<PlanValue>(() => {
    const raw = me?.settings?.industry;
    const industry: Industry = raw === 'computer' || raw === 'auto' || raw === 'vacuum' ? raw : 'cellphone';
    // The kind of shop comes from the store's own settings (West Coast Wireless and the phone demo: a phone shop).
    return { ...info, subscriber, industry, has: (f) => !subscriber || info.home || info.features.includes(f), reload: load };
  }, [info, subscriber, me?.settings?.industry, load]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePlan(): PlanValue {
  return useContext(Ctx);
}

/** Job words by kind of shop: a mechanic writes work orders for vehicles; everyone else repair tickets. */
export function jobWords(industry: Industry) {
  if (industry === 'auto') return { job: 'work order', jobs: 'work orders', Job: 'Work order', Jobs: 'Work orders', item: 'vehicle', Item: 'Vehicle', checkIn: 'New work order' };
  const item = industry === 'computer' ? 'computer' : industry === 'vacuum' ? 'vacuum' : 'device';
  return { job: 'repair', jobs: 'repairs', Job: 'Repair', Jobs: 'Repairs', item, Item: item[0].toUpperCase() + item.slice(1), checkIn: `Check in a ${item}` };
}
